/* eslint-disable react-refresh/only-export-components */
/**
 * Studio Routes
 *
 * Internal Track15 production tooling cluster, gated to allowlisted
 * Track15 users only (VITE_STUDIO_ALLOWED_EMAILS).
 *
 * StudioAccessGate handles both the unauthenticated case and the
 * non-allowlisted case by rendering NotFoundPage. No /login redirect
 * on /studio/* paths — that would leak the surface's existence to
 * non-allowlisted users (spec §A.4).
 *
 * Slice 1 shipped /studio/audio (dashboard empty-state shell).
 * Slice 2 adds /studio/audio/new and the data-fetch wiring.
 * Slice 3 adds /studio/audio/:briefId.
 *
 * R&CE Phase 3c adds /studio/research/* (inbox + item detail + run history),
 * a sibling surface upstream of audio production.
 */

import { lazy, Suspense } from "react";
import { Navigate, Route } from "react-router-dom";

import { StudioAccessGate } from "@/components/access/StudioAccessGate";

const AudioStudioDashboardPage = lazy(
  () => import("@/features/studio/audio/AudioStudioDashboardPage"),
);

const AudioBriefNewPage = lazy(
  () => import("@/features/studio/audio/AudioBriefNewPage"),
);

const AudioBriefDetailPage = lazy(
  () => import("@/features/studio/audio/AudioBriefDetailPage"),
);

const ResearchInboxPage = lazy(
  () => import("@/features/research/ResearchInboxPage"),
);

const ResearchItemDetailPage = lazy(
  () => import("@/features/research/ResearchItemDetailPage"),
);

const ResearchRunsPage = lazy(
  () => import("@/features/research/ResearchRunsPage"),
);

const BriefsDashboardPage = lazy(
  () => import("@/features/studio/briefs/BriefsDashboardPage"),
);
const BriefNewPage = lazy(
  () => import("@/features/studio/briefs/BriefNewPage"),
);
const BriefEditorPage = lazy(
  () => import("@/features/studio/briefs/BriefEditorPage"),
);

const LinkedInDashboardPage = lazy(
  () => import("@/features/studio/linkedin/LinkedInDashboardPage"),
);
const LinkedInEditorPage = lazy(
  () => import("@/features/studio/linkedin/LinkedInEditorPage"),
);

const PageLoader = () => (
  <div className="flex items-center justify-center p-8">
    <div className="text-gray-600">Loading…</div>
  </div>
);

export function studioRoutes() {
  return (
    <>
      <Route
        path="/studio/audio"
        element={
          <StudioAccessGate>
            <Suspense fallback={<PageLoader />}>
              <AudioStudioDashboardPage />
            </Suspense>
          </StudioAccessGate>
        }
      />
      <Route
        path="/studio/audio/new"
        element={
          <StudioAccessGate>
            <Suspense fallback={<PageLoader />}>
              <AudioBriefNewPage />
            </Suspense>
          </StudioAccessGate>
        }
      />
      <Route
        path="/studio/audio/:briefId"
        element={
          <StudioAccessGate>
            <Suspense fallback={<PageLoader />}>
              <AudioBriefDetailPage />
            </Suspense>
          </StudioAccessGate>
        }
      />
      <Route
        path="/studio/research"
        element={<Navigate to="/studio/research/inbox" replace />}
      />
      <Route
        path="/studio/research/inbox"
        element={
          <StudioAccessGate>
            <Suspense fallback={<PageLoader />}>
              <ResearchInboxPage />
            </Suspense>
          </StudioAccessGate>
        }
      />
      <Route
        path="/studio/research/inbox/:itemId"
        element={
          <StudioAccessGate>
            <Suspense fallback={<PageLoader />}>
              <ResearchItemDetailPage />
            </Suspense>
          </StudioAccessGate>
        }
      />
      <Route
        path="/studio/research/runs"
        element={
          <StudioAccessGate>
            <Suspense fallback={<PageLoader />}>
              <ResearchRunsPage />
            </Suspense>
          </StudioAccessGate>
        }
      />
      <Route
        path="/studio/briefs"
        element={
          <StudioAccessGate>
            <Suspense fallback={<PageLoader />}>
              <BriefsDashboardPage />
            </Suspense>
          </StudioAccessGate>
        }
      />
      <Route
        path="/studio/briefs/new"
        element={
          <StudioAccessGate>
            <Suspense fallback={<PageLoader />}>
              <BriefNewPage />
            </Suspense>
          </StudioAccessGate>
        }
      />
      <Route
        path="/studio/briefs/:briefId"
        element={
          <StudioAccessGate>
            <Suspense fallback={<PageLoader />}>
              <BriefEditorPage />
            </Suspense>
          </StudioAccessGate>
        }
      />
      <Route
        path="/studio/linkedin"
        element={
          <StudioAccessGate>
            <Suspense fallback={<PageLoader />}>
              <LinkedInDashboardPage />
            </Suspense>
          </StudioAccessGate>
        }
      />
      <Route
        path="/studio/linkedin/:postId"
        element={
          <StudioAccessGate>
            <Suspense fallback={<PageLoader />}>
              <LinkedInEditorPage />
            </Suspense>
          </StudioAccessGate>
        }
      />
    </>
  );
}
